import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  InputSignal,
  OnInit,
} from '@angular/core';
import { ControlErrorDirective } from '../../../directive/control-error.directive';
import {
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { BeneficiaryDetailsFormModel } from './form-group/beneficiary-details-form.model';
import { getMartialStatus } from '../../../enums/stan-cywilny.enum';
import { getGenderDropdownItems } from '../../../enums/plec.enum';
import {
  getDocumentTypeDropdownItems,
  RodzajDokumentu,
} from '../../../enums/rodzaj-dokumentu.enum';
import { SlownikiApi } from '../../../services/api/slowniki.api';
import { SlownikRodzaj } from '../../../enums/slownik-rodzaj.enum';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ErrorSummary } from '../../error-summary/error-summary';
import { ErrorMessagesPipe } from '../../../pipes/error-messages.pipe';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import { PeselUtils } from '../../../utils/pesel.utils';
import { InfoMessageComponent } from '../../info-message/info-message.component';
import { RequiredLabelDirective } from '../../../directive/required-label.directive';
import { DowodOsobistyValidator } from '../../../validators/dowod-osobisty.validator';
import { DatePicker } from 'primeng/datepicker';
import { HttpResponseService } from '../../../../core/services/http-error-response.service';

@Component({
  selector: 'app-form-beneficiary-details',
  imports: [
    ControlErrorDirective,
    FormsModule,
    InputText,
    Select,
    ReactiveFormsModule,
    ErrorSummary,
    ErrorMessagesPipe,
    InfoMessageComponent,
    RequiredLabelDirective,
    DatePicker,
  ],
  templateUrl: './form-beneficiary-details.component.html',
  styleUrl: './form-beneficiary-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormBeneficiaryDetailsComponent implements OnInit {
  public formGroup = input.required<FormGroup<BeneficiaryDetailsFormModel>>();
  public readonlyControls: InputSignal<(keyof BeneficiaryDetailsFormModel)[]> =
    input<(keyof BeneficiaryDetailsFormModel)[]>([
      'imie',
      'nazwisko',
      'pesel',
      'dataUrodzenia',
    ]);
  public familyMember = input<boolean>(false);
  readonly #destroyRef = inject(DestroyRef);
  readonly #httpResponseService = inject(HttpResponseService);
  protected readonly martialStatusItems = getMartialStatus();
  protected readonly genderItems = getGenderDropdownItems();
  protected readonly documentTypeItems = getDocumentTypeDropdownItems();
  readonly #slownikiApi = inject(SlownikiApi);
  protected readonly obywatelstwa = toSignal(
    this.#slownikiApi
      .dajListePozycjiSlownika$({
        kodSlownika: SlownikRodzaj.T_SPANSTW_OBYWATELSTW_PL,
      })
      .pipe(
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas pobierania niezbędnych danych do wypełnienia formularza',
          );
          return EMPTY;
        }),
        finalize(() => this.#setDefaultObywatelstwoPolskie()),
      ),
  );
  maxDate: Date | undefined;

  ngOnInit(): void {
    this.#setDataUrodzeniaFromPesel();
    this.#subscribeToPeselValueChanges();
    this.#subscribeToRodzajDokumentuValueChanges();
    this.#subscribeToNrDokumentuValueChanges();
    this.maxDate = new Date();

    this.#refreshNrDokumentuValidators();
    this.#refreshRodzajDokumentuValidators();
  }

  #subscribeToPeselValueChanges(): void {
    this.formGroup()
      .controls.pesel.valueChanges.pipe(
        tap(() => this.#setDataUrodzeniaFromPesel()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #subscribeToRodzajDokumentuValueChanges(): void {
    this.formGroup()
      .controls.rodzajDokumentu.valueChanges.pipe(
        tap(() => this.#refreshNrDokumentuValidators()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #subscribeToNrDokumentuValueChanges(): void {
    this.formGroup()
      .controls.nrDokumentu.valueChanges.pipe(
        tap(() => this.#refreshRodzajDokumentuValidators()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #setDataUrodzeniaFromPesel(): void {
    const decodePesel = PeselUtils.decode(
      this.formGroup().controls.pesel.value,
    );
    if (decodePesel.isValid) {
      this.formGroup().controls.dataUrodzenia.setValue(
        this.#convertToUTC(decodePesel.birthDate)
      );
      this.formGroup().controls.dataUrodzenia.disable();
    } else {
      this.formGroup().controls.dataUrodzenia.enable();
    }
  }

  #setDefaultObywatelstwoPolskie(): void {
    const obywatelstwa = this.obywatelstwa();
    if (
      !obywatelstwa ||
      this.formGroup().controls.obywatelstwo.value !== null
    ) {
      return;
    }

    const obywatelstwoPL = obywatelstwa.find((value) => value.kod === 'PL');
    if (obywatelstwoPL) {
      this.formGroup().controls.obywatelstwo.setValue(obywatelstwoPL);
    }
  }

  #refreshNrDokumentuValidators(): void {
    const nrDokumentu = this.formGroup().controls.nrDokumentu;
    const rodzajDokumentuValue =
      this.formGroup().controls.rodzajDokumentu.value;

    nrDokumentu.clearValidators();

    if (rodzajDokumentuValue) {
      nrDokumentu.addValidators(Validators.required);
    }

    if (rodzajDokumentuValue === RodzajDokumentu.DOWOD_OSOBISTY) {
      nrDokumentu.addValidators(
        DowodOsobistyValidator.validNumberDokumentuTozsamosci,
      );
    }

    nrDokumentu.updateValueAndValidity({
      onlySelf: true,
      emitEvent: false,
    });
    nrDokumentu.markAsUntouched();
  }

  #refreshRodzajDokumentuValidators(): void {
    const rodzajDokumentu = this.formGroup().controls.rodzajDokumentu;
    const nrDokumentuValue = this.formGroup().controls.nrDokumentu.value;

    rodzajDokumentu.clearValidators();

    if (nrDokumentuValue) {
      rodzajDokumentu.addValidators(Validators.required);
    }

    rodzajDokumentu.updateValueAndValidity({
      onlySelf: true,
      emitEvent: false,
    });
    rodzajDokumentu.markAsUntouched();
  }

  #convertToUTC(date: Date | null): Date | null {
    if (!date) {
      return null;
    }
    const timeDifference = date.getTimezoneOffset();
    date.setHours(0, 0, 0, 0);
    return new Date(date.setMinutes(-timeDifference));
  }
}
